import { skipToken, useSuspenseQuery } from '@apollo/client/react';
import { Box, Button, Heading, Table, Text, VStack } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import { JsonReadonlyBlock } from '#components/json-readonly-block/json-readonly-block';
import { PageContentRoot } from '#components/page-content-root/page-content-root';
import { GET_PROCESSOR_INSTANCE } from '#graphql/queries/processor-instance';
import { useAppConfig } from '#providers/app-config-provider';

import { ProcessorInstanceLogViewer } from './processor-instance-log-viewer';

export function ProcessorInstanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { config } = useAppConfig();
  const instanceId = id ?? '';

  const { data, error, refetch } = useSuspenseQuery(
    GET_PROCESSOR_INSTANCE,
    !instanceId
      ? skipToken
      : {
          fetchPolicy: 'cache-and-network',
          variables: { id: instanceId },
        },
  );

  if (!instanceId) {
    return (
      <PageContentRoot>
        <Text color="fg.muted">Missing instance id.</Text>
      </PageContentRoot>
    );
  }

  if (error) {
    return (
      <PageContentRoot>
        <Text color="red.500">Error: {error.message}</Text>
        <Button onClick={() => void refetch()} mt={4}>
          Retry
        </Button>
      </PageContentRoot>
    );
  }

  const row = data?.processorInstance;
  const auditRows = row?.aiAudits ?? [];
  if (!row) {
    return (
      <PageContentRoot>
        <Heading size="md" mb={2}>
          Not found
        </Heading>
        <Text color="fg.muted">
          No processor instance for id{' '}
          <Text as="span" fontFamily="mono">
            {instanceId}
          </Text>
          .
        </Text>
      </PageContentRoot>
    );
  }

  return (
    <PageContentRoot flex={1} minH={0} display="flex" flexDirection="column">
      <VStack gap={4} align="stretch">
        <Box flexShrink={0}>
          <Heading size="lg" color="fg">
            Processor instance
          </Heading>
          <VStack gap={1} align="stretch" mt={3}>
            <Field label="id" value={row.id} mono />
            <Field label="processorId" value={row.processorId} mono />
            <Field label="userId" value={row.userId} mono />
            <Field label="status" value={row.status} mono />
            <Field
              label="createdAt"
              value={new Date(row.createdAt).toLocaleString()}
            />
            <Field
              label="updatedAt"
              value={new Date(row.updatedAt).toLocaleString()}
            />
            <Box>
              <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                error
              </Text>
              {row.error != null && row.error !== '' ? (
                <Box
                  as="pre"
                  fontSize="sm"
                  fontFamily="mono"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  mt={1}
                >
                  {row.error}
                </Box>
              ) : (
                <Text fontSize="sm" color="fg.muted" mt={1}>
                  —
                </Text>
              )}
            </Box>
          </VStack>
        </Box>
        <Box flexShrink={0}>
          {row.metadata != null ? (
            <JsonReadonlyBlock
              title="metadata"
              value={row.metadata}
              fitContentHeight
            />
          ) : (
            <>
              <Text fontSize="xs" color="fg.muted" fontWeight="medium" mb={1}>
                metadata
              </Text>
              <Text fontSize="sm" color="fg.muted">
                —
              </Text>
            </>
          )}
        </Box>
        <Box flexShrink={0}>
          {row.runLogAvailable ? (
            <ProcessorInstanceLogViewer
              instanceId={instanceId}
              apiHost={config.apiHost}
            />
          ) : (
            <>
              <Text fontSize="xs" color="fg.muted" fontWeight="medium" mb={1}>
                Run log
              </Text>
              <Text fontSize="sm" color="fg.muted">
                No run log available for this instance.
              </Text>
            </>
          )}
        </Box>

        <Box flexShrink={0}>
          <Heading size="md" color="fg" mb={3}>
            AI audit log
          </Heading>
          <Text fontSize="sm" color="fg.muted" mb={3}>
            OpenAI audit rows whose metadata.instanceId (or legacy executionId)
            matches this processor instance.
          </Text>
          {auditRows.length === 0 ? (
            <Text color="fg.muted" fontSize="sm">
              No matching audit rows.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm" variant="line">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Created</Table.ColumnHeader>
                    <Table.ColumnHeader>Endpoint</Table.ColumnHeader>
                    <Table.ColumnHeader>Iteration</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">
                      Token in
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">
                      Token out
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>Id</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {auditRows.map((auditRow) => (
                    <Table.Row key={auditRow.id}>
                      <Table.Cell whiteSpace="nowrap">
                        {new Date(auditRow.createdAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="xs">
                        {auditRow.endpoint}
                      </Table.Cell>
                      <Table.Cell fontSize="sm">
                        {auditRow.iteration != null
                          ? `${auditRow.iteration}${
                              auditRow.maxIterations != null
                                ? ` / ${auditRow.maxIterations}`
                                : ''
                            }`
                          : '—'}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="end"
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {auditRow.tokenIn != null ? auditRow.tokenIn : '—'}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="end"
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {auditRow.tokenOut != null ? auditRow.tokenOut : '—'}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="sm">
                        {auditRow.id}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Box>
      </VStack>
    </PageContentRoot>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" fontWeight="medium">
        {label}
      </Text>
      <Text
        fontSize="sm"
        color="fg"
        fontFamily={mono ? 'mono' : undefined}
        mt={0.5}
      >
        {value}
      </Text>
    </Box>
  );
}
